import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { initSentry, Sentry } from './config/sentry';
import { IS_PRODUCTION } from './config/buildEnv';

initSentry();
import { markNativeShellSync, initializeCapacitorShell } from '../../nexa_mobile/src/capacitorPlatform';
import '../../nexa_mobile/src/capacitor.css';

markNativeShellSync();
initializeCapacitorShell();

const CHUNK_RELOAD_FLAG = 'roomnexa_chunk_reload_attempted';

const reloadOnceForChunkFailure = (reason) => {
  const message = String(reason?.message || reason || '');
  const name = String(reason?.name || '');
  if (!/ChunkLoadError|Loading chunk \d+ failed/i.test(`${name} ${message}`)) {
    return;
  }
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1') {
    return;
  }
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  window.location.reload();
};

window.addEventListener('error', (event) => {
  reloadOnceForChunkFailure(event.error || event.message);
});

window.addEventListener('unhandledrejection', (event) => {
  reloadOnceForChunkFailure(event.reason);
});

window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
});

if (!IS_PRODUCTION) {
  const shouldSuppressRechartsSizeWarning = (args) => {
    const combined = args
      .map((arg) => (typeof arg === 'string' ? arg : ''))
      .join(' ');
    return (
      combined.includes('width(-1)') &&
      combined.includes('height(-1)') &&
      combined.includes('of chart should be greater than 0')
    );
  };

  const originalWarn = console.warn;
  const originalError = console.error;

  console.warn = (...args) => {
    if (shouldSuppressRechartsSizeWarning(args)) return;
    originalWarn(...args);
  };

  console.error = (...args) => {
    if (shouldSuppressRechartsSizeWarning(args)) return;
    originalError(...args);
  };
}

// Auto-reload in development when bundle changes (web only).
// Capacitor native already polls a tiny bundle.version file — avoid HEAD-polling
// the multi‑MB bundle.js every 1.5s on Android/iOS WebViews.
if (!IS_PRODUCTION && typeof document !== 'undefined'
  && !document.documentElement.classList.contains('capacitor-native')) {
  let lastKnownBundleId = '';

  const checkForUpdates = () => {
    // Use a timestamp query parameter to bypass cache and detect bundle changes.
    const timestamp = Date.now();
    fetch(`/static/frontend/bundle.js?t=${timestamp}`, {
      method: 'HEAD',
      cache: 'no-store',
    })
      .then((response) => {
        if (response.ok) {
          const lastModified = response.headers.get('last-modified');
          const contentLength = response.headers.get('content-length');

          // Create a stable identifier for this version of the bundle.
          const bundleId = `${lastModified}-${contentLength}`;

          if (!lastKnownBundleId) {
            lastKnownBundleId = bundleId;
          } else if (lastKnownBundleId !== bundleId) {
            // Bundle has been updated, reload the page.
            window.location.reload();
          }
        }
      })
      .catch(() => {
        // Ignore errors (bundle might not exist yet or CORS issues)
      });
  };

  // Check for updates every 1.5 seconds in development.
  checkForUpdates();
  setInterval(checkForUpdates, 1500);

  // Also check immediately when the tab regains focus.
  window.addEventListener('focus', checkForUpdates);
}

// Initialize React app
const rootElement = document.getElementById('react-root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <React.StrictMode>
        <App />
      </React.StrictMode>
    </Sentry.ErrorBoundary>
  );
}

